import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { TabNavigation } from "./ui/tab-navigation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import {
  Clock,
  Check,
  X,
  Filter,
  Search,
  History,
  AlertCircle,
  CheckCircle,
  XCircle,
  Timer,
  Loader2,
} from "lucide-react";
import {
  useTimeExtensionRequests,
  useUpdateTimeExtensionStatus,
  useBulkUpdateTimeExtensionStatus,
  type TimeExtensionRequest,
} from "../api/timeExtensionApi";
import { PageHeader } from "./ui/page-header";
import {
  useSendEnhancedMessageMutation,
  type SendDirectMessageRequest,
} from "..//api/CommunicationApi";
import { useAuth } from "../App";

export default function TimeExtensionManagement({
  userRole,
}: {
  userRole: string;
}) {
  const [selectedTab, setSelectedTab] = useState("pending");
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination states
  const [pendingPage, setPendingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [pendingLimit, setPendingLimit] = useState(10);
  const [historyLimit, setHistoryLimit] = useState(10);

  // Modal states
  const [selectedRequest, setSelectedRequest] =
    useState<TimeExtensionRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<"approve" | "reject">("approve");

  // Form states for request processing
  const [customDuration, setCustomDuration] = useState("");
  const [customDurationMinutes, setCustomDurationMinutes] = useState("");
  const [reviewComments, setReviewComments] = useState("");

  // Mutation for updating time extension status
  const updateStatusMutation = useUpdateTimeExtensionStatus();
  const bulkUpdateStatusMutation = useBulkUpdateTimeExtensionStatus();
  const sendMessageMutation = useSendEnhancedMessageMutation();
  const { user } = useAuth();

  // Reset pending page when filters change (not status since it doesn't apply to pending)
  useEffect(() => {
    setPendingPage(1);
  }, [searchTerm, courseFilter, projectFilter]);

  // Reset history page when filters change (including status)
  useEffect(() => {
    setHistoryPage(1);
  }, [searchTerm, courseFilter, projectFilter, statusFilter]);

  // Fetch pending requests with status=pending
  const {
    data: pendingRequestsData,
    isLoading: isPendingLoading,
    error: pendingError,
  } = useTimeExtensionRequests(
    {
      status: "pending",
      page: pendingPage,
      limit: pendingLimit,
      ...(searchTerm && { search: searchTerm }),
      ...(courseFilter !== "all" && { course_id: courseFilter }),
      ...(projectFilter !== "all" && { project_id: projectFilter }),
    },
    selectedTab === "pending",
  );

  const {
    data: historyRequestsData,
    isLoading: isHistoryLoading,
    error: historyError,
  } = useTimeExtensionRequests(
    {
      page: historyPage,
      limit: historyLimit,
      ...(searchTerm && { search: searchTerm }),
      ...(courseFilter !== "all" && { course_id: courseFilter }),
      ...(projectFilter !== "all" && { project_id: projectFilter }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    },
    true,
  );

  // Extract data from API responses with proper array checks
  // API returns data in structure: { data: { rows: [...], filters: {...} } }
  const pendingRequests = Array.isArray(pendingRequestsData?.data?.rows)
    ? pendingRequestsData.data.rows
    : [];

  const historyRequests = Array.isArray(historyRequestsData?.data?.rows)
    ? historyRequestsData.data.rows
    : [];

  // Extract pagination data
  const pendingPagination = pendingRequestsData?.data
    ? {
        currentPage: pendingRequestsData.data.page,
        totalPages: pendingRequestsData.data.totalPages,
        totalItems: pendingRequestsData.data.count,
      }
    : null;

  const historyPagination = historyRequestsData?.data
    ? {
        currentPage: historyRequestsData.data.page,
        totalPages: historyRequestsData.data.totalPages,
        totalItems: historyRequestsData.data.count,
      }
    : null;

  // Use appropriate loading and error states based on selected tab
  const isLoading =
    selectedTab === "pending" ? isPendingLoading : isHistoryLoading;
  const error = selectedTab === "pending" ? pendingError : historyError;

  const uniqueCourses = useMemo(() => {
    // Use filters from API response if available
    const pendingCourses = pendingRequestsData?.data?.filters?.courses || [];
    const historyCourses = historyRequestsData?.data?.filters?.courses || [];
    const allCourses = [...pendingCourses, ...historyCourses];

    // Create unique list based on course_id
    const uniqueMap = new Map();
    allCourses.forEach((course) => {
      if (course.course_id && !uniqueMap.has(course.course_id)) {
        uniqueMap.set(course.course_id, {
          id: course?.course_id,
          name: course?.name,
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [pendingRequestsData, historyRequestsData]);

  const uniqueProjects = useMemo(() => {
    const pendingProjects = pendingRequestsData?.data?.filters?.projects || [];
    const historyProjects = historyRequestsData?.data?.filters?.projects || [];
    const allProjects = [...pendingProjects, ...historyProjects];

    const uniqueMap = new Map();
    allProjects.forEach((project) => {
      if (project.id && !uniqueMap.has(project.id)) {
        uniqueMap.set(project.id, {
          id: project.id,
          name: project.title,
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [pendingRequestsData, historyRequestsData]);

  const filteredPendingRequests = pendingRequests;

  const filteredHistoryRequests = historyRequests;

  const filteredRequests =
    selectedTab === "pending"
      ? filteredPendingRequests
      : filteredHistoryRequests;

  const handleSelectRequest = (requestId: string, checked: boolean) => {
    if (checked) {
      setSelectedRequests((prev) => [...prev, requestId]);
    } else {
      setSelectedRequests((prev) => prev.filter((id) => id !== requestId));
    }
  };

  const handleSelectAllRequests = (checked: boolean) => {
    if (checked) {
      setSelectedRequests(filteredRequests.map((req) => req.id));
    } else {
      setSelectedRequests([]);
    }
  };

  const handleViewDetails = (request: TimeExtensionRequest) => {
    setSelectedRequest(request);
    // Parse duration_value (format: "030:00" for 30 hours and 0 minutes)
    const parts = request.duration_value.split(":");
    const hoursStr = parts[0] ?? "0";
    const minutesStr = parts[1] ?? "0";
    setCustomDuration(parseInt(hoursStr, 10).toString());
    setCustomDurationMinutes(parseInt(minutesStr, 10).toString());
    setReviewComments("");
    setShowDetailsModal(true);
  };

  const sendTimeExtensionDecisionMessage = async (params: {
    recipientId: string;
    studentName: string;
    projectName?: string;
    status: "approve" | "reject";
    durationValue?: string;
    reviewerName?: string;
    reviewComments?: string | null;
  }) => {
    const {
      recipientId,
      studentName,
      projectName,
      status,
      durationValue,
      reviewerName,
      reviewComments,
    } = params;
    let messageContent;
    if (status === "approve") {
      messageContent = ` Sandbox extension ${"approved"}

  Hi ${studentName},
  Duration: ${durationValue} | Project: ${projectName} | Approved by ${reviewerName} on ${new Date().toLocaleString("en-IN")}.
  Instructor comment: ${reviewComments}`;
    } else {
      messageContent = ` Sandbox extension ${"rejected"}
    Instructor comment: ${reviewComments}`;
    }

    const payload: SendDirectMessageRequest = {
      recipientId,
      subject: `Time extension ${
        status === "approve" ? "approved" : "rejected"
      }`,
      content: messageContent,
    };
    await sendMessageMutation.mutateAsync(payload);
  };

  const handleProcessSingleRequest = async (action: "approve" | "reject") => {
    if (!selectedRequest) return;

    // Validation for approve action
    if (action === "approve") {
      const hours = parseInt(customDuration || "0", 10);
      const minutes = parseInt(customDurationMinutes || "0", 10);

      if (hours === 0 && minutes === 0) {
        toast.error("Please enter a valid value for hours and/or minutes");
        return;
      }
    }

    try {
      // Format duration as "HHH:MM"
      const hours = customDuration || "0";
      const minutes = customDurationMinutes || "0";
      const duration_value = `${hours.padStart(3, "0")}:${minutes.padStart(
        2,
        "0",
      )}`;

      const payload = {
        status:
          action === "approve"
            ? "approved"
            : ("rejected" as "approved" | "rejected"),
        duration_value,
        review_comments: reviewComments,
      };

      await updateStatusMutation.mutateAsync({
        id: selectedRequest.id,
        payload,
      });
      if (selectedRequest.student?.id) {
        await sendTimeExtensionDecisionMessage({
          recipientId: selectedRequest.student.id,
          studentName: selectedRequest.student.name,
          projectName: selectedRequest.sandboxSession?.project?.title,
          status: action,
          durationValue: duration_value,
          reviewerName: user?.name,
          reviewComments,
        });
      }
      toast.success(`Request ${action}d successfully`);
      setShowDetailsModal(false);
      setSelectedRequest(null);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || `Failed to ${action} request`,
      );
      console.error(`Error ${action}ing request:`, error);
    }
  };

  const handleBulkAction = () => {
    if (selectedRequests.length === 0) {
      toast.error("Please select at least one request");
      return;
    }

    setShowBulkModal(true);
  };

  const handleConfirmBulkAction = async () => {
    if (selectedRequests.length === 0) {
      toast.error("No requests selected");
      return;
    }

    // Validation for bulk approve action
    if (bulkAction === "approve") {
      const hours = parseInt(customDuration || "0", 10);
      const minutes = parseInt(customDurationMinutes || "0", 10);

      if (hours === 0 && minutes === 0) {
        toast.error("Please enter a valid value for hours and/or minutes");
        return;
      }
    }

    try {
      // Format duration as "HHH:MM"
      const hours = customDuration || "0";
      const minutes = customDurationMinutes || "0";
      const duration_value = `${hours.padStart(3, "0")}:${minutes.padStart(
        2,
        "0",
      )}`;

      const payload = {
        ids: selectedRequests,
        status:
          bulkAction === "approve"
            ? "approved"
            : ("rejected" as "approved" | "rejected"),
        review_comments: reviewComments || `Bulk ${bulkAction} by instructor`,
        duration_value,
      };

      await bulkUpdateStatusMutation.mutateAsync(payload);

      toast.success(
        `Successfully ${bulkAction}ed ${selectedRequests.length} request(s)`,
      );

      const requestsToNotify = filteredRequests.filter((req) =>
        selectedRequests.includes(req.id),
      );
      console.log("requestsToNotify", requestsToNotify);
      await Promise.all(
        requestsToNotify?.map((req) => {
          if (!req.student?.id) return null;
          return sendTimeExtensionDecisionMessage({
            recipientId: req.student.id,
            studentName: req.student.name,
            projectName: req.sandboxSession?.project?.title,
            status: bulkAction,
            durationValue: duration_value,
            reviewerName: user?.name,
            reviewComments,
          });
        }),
      );

      setSelectedRequests([]);
      setShowBulkModal(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to bulk ${bulkAction} requests`,
      );
      console.error(`Error bulk ${bulkAction}ing requests:`, error);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "expired":
        return "bg-gray-100 text-gray-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDateDDMMYYYY = (dateString: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      case "expired":
        return <Timer className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Time Extension Requests"
        subtitle="Manage student requests for project deadline extensions"
      >
        <div className="flex gap-2">
          <Badge variant="outline" className="text-yellow-600">
            {isPendingLoading ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              filteredPendingRequests.length
            )}{" "}
            Pending
          </Badge>
          <Badge variant="outline" className="text-gray-600">
            {isHistoryLoading ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              filteredHistoryRequests.length
            )}{" "}
            Processed
          </Badge>
        </div>
      </PageHeader>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search students, projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Course</Label>
              <Select value={courseFilter} onValueChange={setCourseFilter}>
                <SelectTrigger className="w-full [&>span]:truncate [&>span]:block">
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {uniqueCourses.map((course) => (
                    <TooltipProvider key={course.id} delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full">
                            <SelectItem
                              value={course.id}
                              className="cursor-pointer"
                            >
                              <span className="truncate block">
                                {course.name}
                              </span>
                            </SelectItem>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="max-w-[400px] z-[100]"
                        >
                          <p className="text-sm">{course.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full [&>span]:truncate [&>span]:block">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {uniqueProjects.map((project) => (
                    <TooltipProvider key={project.id} delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full">
                            <SelectItem
                              value={project.id}
                              className="cursor-pointer"
                            >
                              <span className="truncate block">
                                {project.name}
                              </span>
                            </SelectItem>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="max-w-[400px] z-[100]"
                        >
                          <p className="text-sm">{project.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            {selectedTab === "history" && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={[
          {
            key: "pending",
            label: "Pending Requests",
            icon: <Clock className="w-4 h-4" />,
            count: filteredPendingRequests.length,
          },
          {
            key: "history",
            label: "History",
            icon: <History className="w-4 h-4" />,
            count: filteredHistoryRequests.length,
          },
        ]}
        activeTab={selectedTab}
        onTabChange={setSelectedTab}
        className="mb-6"
      />

      {/* Pending Requests Tab Content */}
      {selectedTab === "pending" && (
        <div className="space-y-4">
          {/* Bulk Actions */}
          {selectedRequests.length > 0 && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">
                      {selectedRequests.length} requests selected
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setBulkAction("approve");
                          handleBulkAction();
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Bulk Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setBulkAction("reject");
                          handleBulkAction();
                        }}
                        className="bg-red-500 hover:bg-red-500"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Bulk Reject
                      </Button>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedRequests([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Requests Table */}
          <Card>
            <CardHeader>
              <CardTitle>Pending Extension Requests</CardTitle>
              <CardDescription>
                Review and process student requests for project deadline
                extensions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={
                            selectedRequests.length ===
                              filteredRequests.length &&
                            filteredRequests.length > 0
                          }
                          onCheckedChange={handleSelectAllRequests}
                        />
                      </TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                          <p className="text-sm text-gray-500 mt-2">
                            Loading requests...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          <AlertCircle className="w-6 h-6 text-red-500 mx-auto" />
                          <p className="text-sm text-red-500 mt-2">
                            Request not found!
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : filteredRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          <p className="text-sm text-gray-500">
                            No pending requests found
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedRequests.includes(request.id)}
                              onCheckedChange={(checked) =>
                                handleSelectRequest(
                                  request.id,
                                  checked as boolean,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">
                                {request.student?.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {request.student?.email}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {request.sandboxSession?.project?.course?.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {request.sandboxSession?.project?.title}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {request.duration_value}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {new Date(
                                request.created_at,
                              ).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetails(request)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination for Pending Requests */}
              {pendingPagination && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-bits-grey-600">
                    Page {pendingPagination.currentPage} of{" "}
                    {pendingPagination.totalPages} •{" "}
                    {pendingPagination.totalItems} items
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pendingPage <= 1}
                      onClick={() => setPendingPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pendingPage >= pendingPagination.totalPages}
                      onClick={() => setPendingPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                    <Select
                      value={String(pendingLimit)}
                      onValueChange={(v) => {
                        setPendingLimit(Number(v));
                        setPendingPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 20, 30, 50].map((l) => (
                          <SelectItem key={l} value={String(l)}>
                            {l} / page
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Tab Content */}
      {selectedTab === "history" && (
        <div className="space-y-4">
          {/* History Table */}
          <Card>
            <CardHeader>
              <CardTitle>Extension Request History</CardTitle>
              <CardDescription>
                View processed extension requests and their outcomes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Processed By</TableHead>
                      <TableHead>Reviewed At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                          <p className="text-sm text-gray-500 mt-2">
                            Loading history...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <AlertCircle className="w-6 h-6 text-red-500 mx-auto" />
                          <p className="text-sm text-red-500 mt-2">
                            Failed to load history
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : filteredRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <p className="text-sm text-gray-500">
                            No history found
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">
                                {request.student?.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {request.student?.email}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {request.sandboxSession?.project?.title}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {request.duration_value}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={getStatusBadgeColor(request.status)}
                            >
                              <div className="flex items-center gap-1">
                                {getStatusIcon(request.status)}
                                {request.status}
                              </div>
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {request.reviewer?.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {formatDateDDMMYYYY(request.reviewed_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetails(request)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination for History Requests */}
              {historyPagination && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-bits-grey-600">
                    Page {historyPagination.currentPage} of{" "}
                    {historyPagination.totalPages} •{" "}
                    {historyPagination.totalItems} items
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={historyPage <= 1}
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={historyPage >= historyPagination.totalPages}
                      onClick={() => setHistoryPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                    <Select
                      value={String(historyLimit)}
                      onValueChange={(v) => {
                        setHistoryLimit(Number(v));
                        setHistoryPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 20, 30, 50].map((l) => (
                          <SelectItem key={l} value={String(l)}>
                            {l} / page
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Request Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-[60vw] min-w-[50vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Extension Request Details</DialogTitle>
            <DialogDescription>
              Review and process the extension request
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Student and Project Info */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Student
                    </Label>
                    <div className="mt-1">
                      <div className="font-medium">
                        {selectedRequest.student?.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedRequest.student?.email}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Course
                    </Label>
                    <div className="mt-1 font-medium">
                      {selectedRequest?.sandboxSession?.project?.course?.name}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Project
                    </Label>
                    <div className="mt-1 font-medium">
                      {selectedRequest.sandboxSession?.project?.title}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Requested Duration
                    </Label>
                    <div className="mt-1">
                      <Badge variant="outline" className="text-sm">
                        {selectedRequest.duration_value}
                      </Badge>
                    </div>
                  </div>
                  <div className="pt-3">
                    <Label className="text-sm font-medium text-gray-500 ">
                      Submitted Date
                    </Label>
                    <div className="mt-1 font-medium">
                      {new Date(selectedRequest.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-sm font-medium text-gray-500">
                  Reason for Extension
                </Label>
                <div className="mt-2 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm">{selectedRequest.reason}</p>
                </div>
              </div>

              {/* Show review information if processed */}
              {selectedRequest.status !== "pending" && (
                <div className="border-t pt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Status
                      </Label>
                      <div className="mt-1">
                        <Badge
                          className={getStatusBadgeColor(
                            selectedRequest.status,
                          )}
                        >
                          {selectedRequest.status}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Processed By
                      </Label>
                      <div className="mt-1 font-medium">
                        {selectedRequest.reviewer?.name}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Processed Date
                      </Label>
                      <div className="mt-1 font-medium">
                        {selectedRequest.reviewed_at
                          ? new Date(
                              selectedRequest.reviewed_at,
                            ).toLocaleString()
                          : ""}
                      </div>
                    </div>
                    {selectedRequest.approved_due_date && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Approved Due Date
                        </Label>
                        <div className="mt-1 font-medium">
                          {new Date(
                            selectedRequest.approved_due_date,
                          ).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedRequest.review_comments && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Review Comments
                      </Label>
                      <div className="mt-2 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm">
                          {selectedRequest.review_comments}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Only show processing controls for pending requests */}
              {selectedRequest.status === "pending" && (
                <>
                  {/* Duration Decision */}
                  <div className="space-y-4 border-t pt-4">
                    <Label className="text-sm font-medium">
                      Grant Extension Duration
                    </Label>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">Hours</Label>
                        <Input
                          type="text"
                          value={customDuration}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers
                            if (value === "" || /^\d+$/.test(value)) {
                              setCustomDuration(value);
                            }
                          }}
                          placeholder="Enter hours"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">Minutes</Label>
                        <Input
                          type="text"
                          value={customDurationMinutes}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and limit to 0-59
                            if (
                              value === "" ||
                              (/^\d+$/.test(value) && parseInt(value) <= 59)
                            ) {
                              setCustomDurationMinutes(value);
                            }
                          }}
                          placeholder="Enter minutes"
                          maxLength={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Review Comments */}
                  <div className="space-y-2">
                    <Label>Review Comments</Label>
                    <Textarea
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      placeholder="Add comments for the student..."
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* Show decision info for processed requests */}
              {selectedRequest.status !== "pending" && (
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Decision
                    </Label>
                    <div className="mt-1">
                      <Badge
                        className={getStatusBadgeColor(selectedRequest.status)}
                      >
                        <div className="flex items-center gap-1">
                          {getStatusIcon(selectedRequest.status)}
                          {selectedRequest.status}
                        </div>
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedRequest?.status === "pending" ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="bg-bits-red"
                  onClick={() => handleProcessSingleRequest("reject")}
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleProcessSingleRequest("approve")}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Bulk {bulkAction === "approve" ? "Approve" : "Reject"} Requests
            </DialogTitle>
            <DialogDescription>
              You are about to {bulkAction} {selectedRequests.length} extension
              requests.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Selected Requests: {selectedRequests.length}</Label>
              <div className="text-sm text-gray-500">
                This action cannot be undone. All selected requests will be{" "}
                {bulkAction === "approve" ? "approved" : "rejected"}.
              </div>
            </div>

            {bulkAction === "approve" && (
              <div className="space-y-2">
                <Label>Default Extension Duration</Label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Hours</Label>
                    <Input
                      type="text"
                      value={customDuration}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers
                        if (value === "" || /^\d+$/.test(value)) {
                          setCustomDuration(value);
                        }
                      }}
                      placeholder="Enter hours"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Minutes</Label>
                    <Input
                      type="text"
                      value={customDurationMinutes}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers and limit to 0-59
                        if (
                          value === "" ||
                          (/^\d+$/.test(value) && parseInt(value) <= 59)
                        ) {
                          setCustomDurationMinutes(value);
                        }
                      }}
                      placeholder="Enter minutes"
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Bulk Review Comments</Label>
              <Textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder="Add comments that will be sent to all selected students..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBulkAction}
              className={
                bulkAction === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-500 hover:bg-red-500"
              }
              variant={bulkAction === "reject" ? "destructive" : "default"}
            >
              {bulkAction === "approve" ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Bulk Approve
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-1" />
                  Bulk Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
